import React, { useRef, useState, useCallback } from 'react';
import { cn } from '../../utils/tw';
import type { BillUploadState, ParsedBillData, MatchedBillData } from '../../types/billParser';
import { parseBillWithAI, matchBillDataWithEntities } from '../../services/billParserService';

interface BillUploadProps {
  onParsed: (matchedData: MatchedBillData, file: File) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  // Entity data for matching
  entities: {
    types: { id: string; name: string }[];
    units: { id: string; name: string; typeId?: string }[];
    costCenters: { id: string; name: string; typeId?: string }[];
    categories: { id: string; name: string; costCenterId?: string }[];
    parties: { id: string; name: string; type: string }[];
  };
}

export const BillUpload: React.FC<BillUploadProps> = ({
  onParsed,
  onError,
  disabled = false,
  entities,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<BillUploadState>({
    file: null,
    preview: null,
    status: 'idle',
  });
  const [dragActive, setDragActive] = useState(false);

  const acceptedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
  const maxSizeInMB = 10;

  const resetState = useCallback(() => {
    setState({
      file: null,
      preview: null,
      status: 'idle',
    });
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    // Validate file size
    if (file.size > maxSizeInMB * 1024 * 1024) {
      const error = `File too large. Maximum size is ${maxSizeInMB}MB`;
      setState({ file: null, preview: null, status: 'error', error });
      onError?.(error);
      return;
    }

    // Validate file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      const error = `Invalid file type. Accepted: ${acceptedTypes.join(', ')}`;
      setState({ file: null, preview: null, status: 'error', error });
      onError?.(error);
      return;
    }

    // Create preview for images
    let preview: string | null = null;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }

    setState({
      file,
      preview,
      status: 'parsing',
    });

    try {
      // Parse the bill using AI
      const parsedData = await parseBillWithAI(file);

      // Match parsed data with app entities
      const matchedData = matchBillDataWithEntities(parsedData, entities);

      setState((prev) => ({
        ...prev,
        status: 'parsed',
        parsedData,
        matchedData,
      }));

      // Notify parent component
      onParsed(matchedData, file);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to parse bill';
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: errorMessage,
      }));
      onError?.(errorMessage);
    }
  }, [entities, onParsed, onError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || state.status === 'parsing') return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderContent = () => {
    switch (state.status) {
      case 'parsing':
        return (
          <div className="space-y-3 py-4">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
            <div className="text-sm text-gray-600">Analyzing bill...</div>
            <div className="text-xs text-mutedText">
              Extracting vendor, items, and amounts
            </div>
          </div>
        );

      case 'parsed':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-success">
              <span className="text-xl">✓</span>
              <span className="font-medium">Bill parsed successfully!</span>
            </div>
            {state.parsedData && (
              <div className="text-left text-sm space-y-1 bg-gray-50 rounded p-3">
                {state.parsedData.vendorName && (
                  <div>
                    <span className="text-mutedText">Vendor:</span>{' '}
                    <span className="font-medium">{state.parsedData.vendorName}</span>
                  </div>
                )}
                {state.parsedData.totalAmount && (
                  <div>
                    <span className="text-mutedText">Total:</span>{' '}
                    <span className="font-medium">
                      ₹{state.parsedData.totalAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
                {state.parsedData.invoiceDate && (
                  <div>
                    <span className="text-mutedText">Date:</span>{' '}
                    <span className="font-medium">{state.parsedData.invoiceDate}</span>
                  </div>
                )}
                {state.parsedData.lineItems.length > 0 && (
                  <div>
                    <span className="text-mutedText">Items:</span>{' '}
                    <span className="font-medium">{state.parsedData.lineItems.length}</span>
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={resetState}
              className="text-sm text-primary hover:underline"
            >
              Upload a different bill
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-error">
              <span className="text-xl">✕</span>
              <span className="font-medium">Failed to parse bill</span>
            </div>
            <div className="text-sm text-error">{state.error}</div>
            <button
              type="button"
              onClick={resetState}
              className="text-sm text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        );

      default:
        return (
          <div className="space-y-2">
            <div className="text-3xl">📄</div>
            <div className="text-sm">
              <span className="font-medium text-primary">Upload a bill</span> to auto-fill expense
            </div>
            <div className="text-xs text-mutedText">
              Supports {acceptedTypes.join(', ')} up to {maxSizeInMB}MB
            </div>
            <div className="text-xs text-blue-600 bg-blue-50 rounded px-2 py-1 inline-block">
              AI will extract vendor, amounts, dates & items
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-3">
      {/* Upload Area */}
      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer',
          dragActive && 'border-primary bg-primary/5 scale-[1.02]',
          !dragActive && state.status === 'idle' && 'border-gray-300 hover:border-primary hover:bg-gray-50',
          state.status === 'parsing' && 'border-primary bg-primary/5 cursor-wait',
          state.status === 'parsed' && 'border-success bg-success/5',
          state.status === 'error' && 'border-error bg-error/5',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => {
          if (!disabled && state.status !== 'parsing') {
            if (state.status === 'idle') {
              fileInputRef.current?.click();
            }
          }
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled || state.status === 'parsing'}
          aria-label="Upload bill"
        />

        {/* Preview Image */}
        {state.preview && state.status !== 'error' && (
          <div className="mb-4">
            <img
              src={state.preview}
              alt="Bill preview"
              className="max-h-32 mx-auto rounded shadow-sm"
            />
          </div>
        )}

        {/* File Info */}
        {state.file && state.status !== 'error' && (
          <div className="mb-3 text-xs text-mutedText">
            {state.file.name} ({formatFileSize(state.file.size)})
          </div>
        )}

        {renderContent()}
      </div>
    </div>
  );
};

export default BillUpload;
